import { useState, useEffect } from "react";
import { CyberpassButton } from "../Input";
import toast from "react-hot-toast";
import { useTranslation } from "../i18n";

/**
 * @constant
 * @type {Array<[string, number]>}
 */
const monthsWithDays = [
  ["Январь", 31],
  ["Февраль", 29],
  ["Март", 31],
  ["Апрель", 30],
  ["Май", 31],
  ["Июнь", 30],
  ["Июль", 31],
  ["Август", 31],
  ["Сентябрь", 30],
  ["Октябрь", 31],
  ["Ноябрь", 30],
  ["Декабрь", 31],
];

/**
 * Serialize the input string
 *
 * @param {string} inputString
 * @returns {string}
 */
function serializeInput(inputString) {
  let s = inputString;
  // Replace ", , " pattern with ", "
  s = s.replace(/(?:,\s*){2,}/g, ", ");
  // Remove ", " in the begginig of the line
  s = s.replace(/^, /, "");

  return s;
}

/**
 * Checks the input string by rules that are mentioned inside this function
 *
 * @param {string} inputString
 * @returns {boolean}
 */
function testInput(inputString, t) {
  // Only digits, coma and space are allowed
  if (!/^[\d, ]+$|^$/.test(inputString)) {
    toast.error(t("toast.invalidPattern"), { duration: 600 });
    return false;
  }
  // Can start only with digit
  if (!/\d|^$/.test(inputString)) {
    toast.error(t("toast.invalidPattern"), { duration: 600 });
    return false;
  }
  // Only up to 2 digits, 1 coma, 1 space allowed consequently
  if (/\d{3,}|,{2,}|\s{2,}/.test(inputString)) {
    toast.error(t("toast.invalidPattern"), { duration: 600 });
    return false;
  }
  // Digit -> digit | coma; coma -> space; space -> digit
  if (/\d[^,\d]|,[^ ]| [^0-9]/.test(inputString)) {
    toast.error(t("toast.invalidPattern"), { duration: 600 });
    return false;
  }

  return true;
}

/**
 * Replace "," or ", " in the end of string with ""
 * Format array from that string
 *
 * @param {string} inputString
 * @returns {string[]}
 */
function monthStrToArr(inputString) {
  return inputString === ""
    ? []
    : inputString
        .replace(/,$|, $/, "")
        .split(", ")
        .map((str) => Number(str));
}

/**
 * Check the date is within month range
 *
 * @param {string} inputString
 * @returns {boolean}
 */
function checkDateInRange(inputString, monthIdx, t) {
  const monthLength = monthsWithDays[monthIdx][1];

  const daysArr = monthStrToArr(inputString);
  for (let day of daysArr) {
    if (day > monthLength) {
      toast.error(t("toast.daysExceedMonth"), { duration: 600 });
      return false;
    }
  }
  return true;
}

/**
 * Checks that the string is valid for an input into single month
 *
 * @param {string} inputString
 * @returns {string | null}
 */
function seriaizeAndTestInputString(input, monthIdx, t) {
  const serializedInput = serializeInput(input);
  if (!testInput(serializedInput, t)) {
    return null;
  }
  if (!checkDateInRange(serializedInput, monthIdx, t)) {
    return null;
  }
  return serializedInput;
}

const initialState = new Array(12).fill("", 0);

export default function HolidayForm({ editValue, requestField, stateLifter }) {
  const { t } = useTranslation();
  const [holidays, setHolidays] = useState(() => {
    if (editValue && editValue.length === 12) {
      return editValue.map((row) => (row.length === 0 ? "" : row.join(", ")));
    } else {
      return initialState;
    }
  });

  useEffect(() => {
    stateLifter((prev) => ({
      ...prev,
      [requestField]: stateToArray(holidays),
    }));
  }, [holidays, stateLifter]);

  /**
   * Handles changes to an input element.
   * @param {React.ChangeEvent<HTMLInputElement>} evt The change event object.
   * @param {number} monthIdx
   * @returns {void}
   */
  function changeHandler(evt, monthIdx) {
    const { value: inputValue, name: month } = evt.target;
    const lastInput = evt.nativeEvent.data;
    const holidaysCopy = [...holidays];

    if (!lastInput || lastInput.length === 1) {
      const serializedString = seriaizeAndTestInputString(
        inputValue,
        monthIdx,
        t,
      );
      if (serializedString === null) {
        return;
      }
      holidaysCopy[month] = serializedString;
    } else {
      const splittedArr = lastInput.split("\n").filter((line) => line !== "");
      for (let i = 0; i < splittedArr.length; ++i) {
        const monthString = splittedArr[i];
        const currMonth = monthIdx + i;

        const serializedString = seriaizeAndTestInputString(
          monthString,
          currMonth,
          t,
        );
        if (serializedString === null) {
          return;
        }
        holidaysCopy[currMonth] = [
          ...(holidaysCopy[currMonth] &&
            monthStrToArr(holidaysCopy[currMonth])),
          ...monthStrToArr(serializedString),
        ].join(", ");
      }
    }

    setHolidays(holidaysCopy);
  }

  function formatState(holidays) {
    let holidaysCopy = [...holidays];

    for (let i = 0; i < holidaysCopy.length; ++i) {
      const monthRowArr = monthStrToArr(holidaysCopy[i]);
      const monthNoDuplicates = [...new Set(monthRowArr)];
      monthNoDuplicates.sort((a, b) => Number(a) - Number(b));

      holidaysCopy[i] = monthNoDuplicates.join(", ");
    }

    return holidaysCopy;
  }

  function stateToArray() {
    const formattedHolidays = formatState(holidays);

    for (let i = 0; i < formattedHolidays.length; ++i) {
      formattedHolidays[i] = monthStrToArr(formattedHolidays[i]);
    }

    return formattedHolidays;
  }

  return (
    <div className="flex flex-col gap-3 py-6">
      <p>{t("holidays.instruction")}</p>
      {holidays.map((currMonth, idx) => (
        <label className="grid grid-cols-[1fr_3fr]" key={`${idx}month`}>
          {monthsWithDays[idx][0]}
          <textarea
            value={currMonth}
            name={idx}
            onChange={(e) => changeHandler(e, idx)}
            className="text-black p-1 resize-none h-10 bg-c-gray-500 text-c-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-c-gray-500 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          />
        </label>
      ))}

      <div className="flex justify-between">
        <CyberpassButton
          onClick={() => setHolidays((prev) => formatState(prev))}
        >
          {t("holidays.format")}
        </CyberpassButton>
        <CyberpassButton onClick={() => setHolidays(initialState)}>
          {t("holidays.clear")}
        </CyberpassButton>
      </div>
    </div>
  );
}
